import { Browser } from '@capacitor/browser'
import { supabase } from '../lib/supabase'
import { AUTH_CALLBACK_PATH, NATIVE_AUTH_SCHEME } from '../lib/authRedirect'

type NativeAuthCallback = {
  code: string | null
  error: string | null
  errorDescription: string | null
  accessToken: string | null
  refreshToken: string | null
}

function readHashParams(hash: string) {
  return new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
}

export function parseNativeAuthCallbackUrl(url: string): NativeAuthCallback | null {
  let parsed: URL

  try {
    parsed = new URL(url)
  } catch {
    return null
  }

  if (parsed.protocol !== `${NATIVE_AUTH_SCHEME}:`) {
    return null
  }

  const isAuthCallback =
    (parsed.hostname === 'auth' && parsed.pathname.startsWith('/callback')) ||
    (!parsed.hostname && parsed.pathname.startsWith('/auth/callback'))

  if (!isAuthCallback) {
    return null
  }

  const hashParams = readHashParams(parsed.hash)

  return {
    code: parsed.searchParams.get('code'),
    error: parsed.searchParams.get('error') ?? hashParams.get('error'),
    errorDescription:
      parsed.searchParams.get('error_description') ?? hashParams.get('error_description'),
    accessToken: hashParams.get('access_token'),
    refreshToken: hashParams.get('refresh_token'),
  }
}

function routeToAuthCallback(params?: URLSearchParams) {
  const queryString = params?.toString()
  const path = queryString ? `${AUTH_CALLBACK_PATH}?${queryString}` : AUTH_CALLBACK_PATH

  window.history.replaceState(window.history.state, '', path)
  window.dispatchEvent(new Event('popstate'))
}

function routeToAuthError(error: string, errorDescription?: string | null) {
  const params = new URLSearchParams({ error })
  if (errorDescription) {
    params.set('error_description', errorDescription)
  }
  routeToAuthCallback(params)
}

async function closeOAuthBrowser() {
  try {
    await Browser.close()
  } catch {
    // Browser.close is best-effort and is a no-op on Android.
  }
}

export async function handleNativeAuthCallbackUrl(url: string) {
  const callback = parseNativeAuthCallbackUrl(url)
  if (!callback) {
    return false
  }

  await closeOAuthBrowser()

  if (callback.error) {
    routeToAuthError(callback.error, callback.errorDescription)
    return true
  }

  try {
    if (callback.code) {
      const { error } = await supabase.auth.exchangeCodeForSession(callback.code)
      if (error) throw error
    } else if (callback.accessToken && callback.refreshToken) {
      const { error } = await supabase.auth.setSession({
        access_token: callback.accessToken,
        refresh_token: callback.refreshToken,
      })
      if (error) throw error
    } else {
      routeToAuthError('missing_auth_code', 'The native OAuth callback did not include a session.')
      return true
    }

    routeToAuthCallback()
  } catch (error) {
    console.error('[Auth] Native OAuth callback failed:', error)
    routeToAuthError(
      'auth_failed',
      error instanceof Error ? error.message : 'Native authentication failed.'
    )
  }

  return true
}
